// Full-screen title card (ROUND 2 / FINAL ROUND) with a simple board-shuffle effect.

import { Mesh, MeshBasicMaterial, Object3D, PlaneGeometry, type Texture } from 'three';
import { clamp01, easeInOutCubic, easeOutCubic } from './lib/easing.js';
import { textTexture } from './lib/text-texture.js';
import { COLOR_STAGE_BLUE, FONT_DISPLAY, STAGE_HEIGHT, STAGE_WIDTH } from './lib/theme.js';

export interface RoundTransitionHandle {
  group: Object3D;
  play: (title: string, subtitle?: string) => void;
  hide: () => void;
  tick: (nowMs: number) => void;
  dispose: () => void;
}

const DURATION = 5000;

export function createRoundTransition(): RoundTransitionHandle {
  const group = new Object3D();
  group.name = 'round-transition';
  group.visible = false;

  const scrim = new Mesh(
    new PlaneGeometry(STAGE_WIDTH, STAGE_HEIGHT),
    new MeshBasicMaterial({ color: COLOR_STAGE_BLUE, transparent: true, opacity: 0 }),
  );
  scrim.position.z = -10;
  scrim.renderOrder = 100;
  group.add(scrim);

  const titleMat = new MeshBasicMaterial({ transparent: true, opacity: 0 });
  const titleMesh = new Mesh(new PlaneGeometry(1400, 260), titleMat);
  titleMesh.position.set(0, 40, 0);
  titleMesh.renderOrder = 110;
  group.add(titleMesh);

  const subMat = new MeshBasicMaterial({ transparent: true, opacity: 0 });
  const subMesh = new Mesh(new PlaneGeometry(1200, 90), subMat);
  subMesh.position.set(0, -140, 0);
  subMesh.renderOrder = 110;
  group.add(subMesh);

  let startMs: number | null = null;

  function play(title: string, subtitle?: string): void {
    group.visible = true;
    startMs = performance.now();
    const titleTex: Texture = textTexture(title.toUpperCase(), {
      font: `220px ${FONT_DISPLAY}`,
      color: '#FFC43D',
      width: 1400,
      height: 260,
      weight: 'bold',
      stroke: '#000000',
      strokeWidth: 6,
    });
    if (titleMat.map && titleMat.map !== titleTex) titleMat.map.dispose();
    titleMat.map = titleTex;
    titleMat.needsUpdate = true;

    if (subtitle) {
      const subTex: Texture = textTexture(subtitle, {
        font: `52px ${FONT_DISPLAY}`,
        color: '#FFFFFF',
        width: 1200,
        height: 90,
        weight: 'bold',
      });
      if (subMat.map && subMat.map !== subTex) subMat.map.dispose();
      subMat.map = subTex;
      subMat.needsUpdate = true;
    } else {
      subMat.map = null;
    }
  }

  function hide(): void {
    group.visible = false;
    startMs = null;
  }

  function tick(nowMs: number): void {
    if (!group.visible || startMs === null) return;
    const elapsed = nowMs - startMs;
    // Fade in 0-400, hold, fade out final 600.
    const fadeIn = clamp01(elapsed / 400);
    const fadeOut = 1 - clamp01((elapsed - (DURATION - 600)) / 600);
    const alpha = Math.min(fadeIn, fadeOut);
    (scrim.material as MeshBasicMaterial).opacity = 0.95 * alpha;
    titleMat.opacity = alpha;
    subMat.opacity = alpha;
    const bounce = easeInOutCubic(clamp01(elapsed / 1200));
    titleMesh.scale.setScalar(0.9 + 0.1 * easeOutCubic(bounce));
    if (elapsed >= DURATION) hide();
  }

  return {
    group,
    play,
    hide,
    tick,
    dispose() {
      scrim.geometry.dispose();
      (scrim.material as MeshBasicMaterial).dispose();
      titleMat.dispose();
      titleMesh.geometry.dispose();
      subMat.dispose();
      subMesh.geometry.dispose();
    },
  };
}
